import React, { FC, useCallback, useState } from 'react'
import { Button, KennoFile, TextArea, TextInput } from '@pandiafi/design-system'
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikProps,
  FormikTouched,
  setNestedObjectValues,
} from 'formik'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, Toggle } from '@pandiafi/components'

import { Contract, User, UserMetadata } from '../../../common/validations'
import {
  feedbackAndQuestionNoticeCreateSchema,
  FeedbackAndQuestionNoticeDetails,
} from '../../../common/validations/feedbackAndQuestionNotice'
import FormFieldWrapper from '../../components/FormField'
import { UploadFiles } from '../../components/UploadFiles'
import { validateFormValues } from '../../utils'
import { useAPIPostFeedback } from '../../api/apiHooks/useAPIPostFeedback'
import { FeedbackPreview } from './FeedbackPreview'
import { FeedbackViewState } from './index'
import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import { NoticeStatus } from '../../../common/validations/defectNotice'
import { useAPIUpdateUserDetails } from '../../api/apiHooks/useAPIUpdateUserEmail'

interface FormValues {
  title: string
  description: string
  files: KennoFile[]
  email: string
  phoneNumber: string
  expectsResponse: boolean
}

interface FeedbackFormProps {
  contract: Contract
  user: User
  userMetadata: UserMetadata
  view: FeedbackViewState
  setView: (view: FeedbackViewState) => void
  existingNotice?: FeedbackAndQuestionNoticeDetails
}

export const FeedbackForm: FC<FeedbackFormProps> = ({
  contract,
  user,
  view,
  setView,
  existingNotice,
  userMetadata,
}) => {
  const { t } = useTranslation(['common', 'feedback'])
  const [uploadOngoing, setUploadOngoing] = useState<boolean>(false)
  const queryClient = useQueryClient()

  const { mutate: updateDetails, isPending } = useAPIUpdateUserDetails()

  const { submit, isSubmitting } = useAPIPostFeedback({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`${API_ENDPOINTS.CONTRACTS}/${contract.uuid}/feedbacks`],
      })
      queryClient.invalidateQueries({
        queryKey: [`${API_ENDPOINTS.CONTRACTS}/${contract.uuid}/questions`],
      })
      setView('success')
    },
  })

  const initialValues: FormValues = {
    title: existingNotice?.title ?? '',
    description: existingNotice?.description ?? '',
    files: existingNotice?.files ?? [],
    email: existingNotice?.noticeReporter?.email ?? user.email ?? '',
    phoneNumber:
      existingNotice?.noticeReporter?.phoneNumber ?? user.phoneNumber ?? '',
    expectsResponse: existingNotice?.expectsResponse ?? false,
  }

  const handleOnFilesUploaded = useCallback(
    (newFiles: KennoFile[], form: FormikProps<FormValues>): void => {
      const { files } = form.values
      form.setFieldValue('files', [...files, ...newFiles])
    },
    []
  )

  const handleOnDeleteUploadedFile = useCallback(
    (fileToRemove: KennoFile, form: FormikProps<FormValues>): void => {
      const { files } = form.values
      form.setFieldValue(
        'files',
        (files || []).filter((f) => f.uuid !== fileToRemove.uuid)
      )
    },
    []
  )

  const handleValidate = (formValues: FormValues) => {
    const formattedValues = {
      title: formValues.title,
      description: formValues.description,
      fileUuids: formValues.files.map((f) => f.uuid),
      contractUuid: contract.uuid,
      noticeReporter: {
        phoneNumber: formValues.phoneNumber,
        email: formValues.email,
      },
      expectsResponse: formValues.expectsResponse,
    }

    const validationErrors = validateFormValues(
      formattedValues,
      feedbackAndQuestionNoticeCreateSchema
    )

    return validationErrors
  }

  const handleSubmit = (values: FormValues) => {
    const formattedValues = {
      title: values.title,
      description: values.description,
      fileUuids: values.files.map((f) => f.uuid),
      contractUuid: contract.uuid,
      noticeReporter: {
        phoneNumber: values.phoneNumber,
        email: values.email,
      },
      expectsResponse: values.expectsResponse,
    }

    if (
      values.email !== user.email ||
      values.phoneNumber !== user.phoneNumber
    ) {
      updateDetails(
        {
          email: values.email,
          phoneNumber: values.phoneNumber,
          communicationByEmail: user.communicationByEmail,
          communicationBySms: user.communicationBySms,
          marketingAllowed: user.marketingAllowed,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.USER_PROFILE],
            })
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.USER_METADATA],
            })
          },
        }
      )
    }

    submit(formattedValues)
  }

  const handlePreview = async (form: FormikProps<FormValues>) => {
    const errors = await form.validateForm()

    if (Object.keys(errors).length > 0) {
      await form.setTouched(
        setNestedObjectValues<FormikTouched<FormValues>>(errors, true)
      )
      document
        .querySelector('.validation-error')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo(0, 0)
      setView('preview')
    }
  }

  const loading = isSubmitting || isPending

  return (
    <div className="max-w-2xl">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={handleValidate}
        enableReinitialize
      >
        {(form: FormikProps<FormValues>) => (
          <Form>
            {view === 'preview' ? (
              <FeedbackPreview
                formValues={form.values}
                isSubmitting={loading}
                submit={() => form.submitForm()}
                cancel={() => setView('form')}
              />
            ) : (
              <>
                <Alert variant="info" className="mb-4">
                  {t('common:sensitiveDataWarning')}
                </Alert>
                <Field name="title">
                  {({ field, meta }: FieldProps<FormValues['title']>) => (
                    <FormFieldWrapper
                      name={field.name}
                      label={t('feedback:labels.description')}
                      meta={meta}
                      labelBold
                      required
                    >
                      <TextInput
                        {...field}
                        maxLength={100}
                        value={meta.value}
                        rounded="rounded-top"
                        className="border-common-gray-4"
                        placeholder={t('feedback:placeholders.title')}
                      />
                    </FormFieldWrapper>
                  )}
                </Field>
                <Field name="description">
                  {({ field, meta }: FieldProps<FormValues['description']>) => (
                    <FormFieldWrapper name={field.name} meta={meta}>
                      <TextArea
                        onChange={(e) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        value={meta.value}
                        rounded="rounded-bottom"
                        className="border-common-gray-4 border-t-0 mb-4"
                        placeholder={t('feedback:placeholders.description')}
                        maxLength={2000}
                      />
                    </FormFieldWrapper>
                  )}
                </Field>

                <Field name="files">
                  {({ field, meta }: FieldProps<FormValues['files']>) => (
                    <FormFieldWrapper name={field.name} meta={meta}>
                      <UploadFiles
                        files={meta.value}
                        onFilesUploaded={(files) =>
                          handleOnFilesUploaded(files, form)
                        }
                        onDeleteFile={(file) =>
                          handleOnDeleteUploadedFile(file, form)
                        }
                        onUploadOngoing={(isUploadOngoing) =>
                          setUploadOngoing(isUploadOngoing)
                        }
                      />
                    </FormFieldWrapper>
                  )}
                </Field>

                {t('feedback:texts.expectsResponseInstructions') && (
                  <div className="mt-6">
                    {t('feedback:texts.expectsResponseInstructions')}
                  </div>
                )}

                <Field name="expectsResponse">
                  {({
                    field,
                    meta,
                  }: FieldProps<FormValues['expectsResponse']>) => (
                    <Toggle
                      className="mt-6"
                      {...field}
                      labelPosition="right"
                      checked={meta.value}
                      onChange={(isSelected) =>
                        form.setFieldValue(field.name, isSelected)
                      }
                    >
                      {t('feedback:labels.expectsResponse')}
                    </Toggle>
                  )}
                </Field>

                {form.values.expectsResponse && (
                  <>
                    <Field name="email">
                      {({ field, meta }: FieldProps<FormValues['email']>) => (
                        <FormFieldWrapper
                          name={field.name}
                          label={t('feedback:labels.email')}
                          meta={meta}
                          labelBold
                          required
                        >
                          <TextInput
                            {...field}
                            type="email"
                            value={meta.value}
                            className="border-common-gray-4"
                            placeholder={t('feedback:labels.email')}
                          />
                        </FormFieldWrapper>
                      )}
                    </Field>

                    <Field name="phoneNumber">
                      {({
                        field,
                        meta,
                      }: FieldProps<FormValues['phoneNumber']>) => (
                        <FormFieldWrapper
                          name={field.name}
                          label={t('feedback:labels.phoneNumber')}
                          meta={meta}
                          labelBold
                          required
                        >
                          <TextInput
                            {...field}
                            type="tel"
                            value={meta.value}
                            className="border-common-gray-4"
                            placeholder={t('feedback:labels.phoneNumber')}
                          />
                        </FormFieldWrapper>
                      )}
                    </Field>

                    {t('feedback:texts.contactDetailsUpdateInstructions') && (
                      <div className="mt-6">
                        {t('feedback:texts.contactDetailsUpdateInstructions')}
                      </div>
                    )}
                  </>
                )}

                <div className="mt-16 flex items-center justify-end">
                  {existingNotice?.uuid ? (
                    <Button
                      type="button"
                      variant="cancel"
                      onClick={() => setView('form')}
                    >
                      {t('feedback:actions.back')}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="cancel"
                      onClick={() => {
                        window.scrollTo(0, 0)
                        form.resetForm()
                      }}
                    >
                      {t('feedback:actions.resetForm')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => handlePreview(form)}
                    disabled={
                      uploadOngoing ||
                      loading ||
                      !form.dirty ||
                      existingNotice?.status === NoticeStatus.COMPLETED ||
                      userMetadata.isImpersonator
                    }
                    loading={loading}
                  >
                    {t('feedback:actions.preview')}
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  )
}
